import React from 'react';
import { RC, SC } from './constants';

export function Tag_({
  children,
  color = '#737373',
  bg = '#f5f5f5',
  border = '#e5e5e5',
}: {
  children: React.ReactNode;
  color?: string;
  bg?: string;
  border?: string;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '5px 10px',
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: 0.3,
        color,
        background: bg,
        border: `1px solid ${border}`,
      }}
    >
      {children}
    </span>
  );
}

export function RatingBadge({ rating, size = 'sm' }: { rating: string; size?: 'sm' | 'md' }) {
  const c = RC(rating);
  const display = rating
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: size === 'md' ? '4px 14px' : '3px 9px',
        borderRadius: 999,
        fontSize: size === 'md' ? 13 : 11,
        fontWeight: 700,
        color: c.text,
        background: c.bg,
        border: `1.5px solid ${c.border}`,
      }}
    >
      <span
        style={{ width: 5, height: 5, borderRadius: '50%', background: c.text, flexShrink: 0 }}
      />
      {display}
    </span>
  );
}

export function ScoreRing({
  score,
  size = 90,
  strokeWidth = 8,
  numberSize,
  numberWeight = 800,
  numberColor,
  labelSize,
}: {
  score: number | null;
  size?: number;
  strokeWidth?: number;
  numberSize?: number;
  numberWeight?: number;
  numberColor?: string;
  labelSize?: number;
}) {
  const r = size / 2 - (strokeWidth + 1);
  const circ = 2 * Math.PI * r;
  const lblSize = labelSize ?? Math.max(8, size * 0.1);

  if (score == null) {
    return (
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e5e5" strokeWidth={strokeWidth} />
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span style={{ fontSize: numberSize ?? size * 0.28, fontWeight: numberWeight, color: numberColor ?? '#d4d4d4', lineHeight: 1 }}>—</span>
          <span className="font-semibold text-muted-foreground" style={{ fontSize: lblSize, marginTop: 1 }}>
            SCORE
          </span>
        </div>
      </div>
    );
  }

  const pct = Math.max(0, Math.min(100, score));
  const color = pct >= 70 ? '#059669' : pct >= 45 ? '#d97706' : '#dc2626';
  const dash = (pct / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e5e5" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.8s ease' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: numberSize ?? size * 0.24, fontWeight: numberWeight, color: numberColor ?? color, lineHeight: 1 }}>{pct}</span>
        <span className="font-semibold text-muted-foreground" style={{ fontSize: lblSize, marginTop: 1 }}>
          SCORE
        </span>
      </div>
    </div>
  );
}

export function SentimentMiniBar({
  pos,
  neu,
  neg,
  tox,
}: {
  pos: number;
  neu: number;
  neg: number;
  tox: number;
}) {
  const total = pos + neu + neg + tox || 100;
  return (
    <div style={{ display: 'flex', height: 6, borderRadius: 4, overflow: 'hidden', gap: 1.5 }}>
      {[
        { pct: pos, color: SC.positive },
        { pct: neu, color: SC.neutral },
        { pct: neg, color: SC.negative },
        { pct: tox, color: SC.toxic },
      ].map((s, i) => (
        <div
          key={i}
          style={{
            width: `${(s.pct / total) * 100}%`,
            background: s.color,
            minWidth: s.pct > 1 ? 2 : 0,
          }}
        />
      ))}
    </div>
  );
}

export function SentimentProgressRow({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span className="text-body-sm-medium text-foreground">{label}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color }}>{value.toFixed(1)}%</span>
      </div>
      <div className="overflow-hidden rounded-sm bg-muted" style={{ height: 6 }}>
        <div
          style={{
            height: '100%',
            width: `${Math.min(value, 100)}%`,
            background: color,
            borderRadius: 4,
            transition: 'width 0.8s ease',
          }}
        />
      </div>
    </div>
  );
}
